// Financial calculation utilities

export function calculateGoalMetrics(goal, userProfile) {
  const {
    timeHorizon,
    goalAmount,
    inflationRate,
    expectedReturn,
    taggedAssets = [],
    sipContribution,
    lumpSumContribution,
    whatIfPercentage = 40,
    useIncomePeriod = true
  } = goal;

  // Calculate future value adjusted for inflation
  const futureValue = goalAmount * Math.pow(1 + inflationRate / 100, timeHorizon);

  // ═══════════════════════════════════════════════════════════════════════════
  // Active Income Period Logic (Two-Phase Investment Strategy)
  // ═══════════════════════════════════════════════════════════════════════════
  const activeIncomePeriod = userProfile.retirementAge - userProfile.currentAge;
  const goalTimeHorizon = timeHorizon;

  // Investment Horizon: How long user will actively invest
  const investmentHorizon = useIncomePeriod 
    ? Math.min(goalTimeHorizon, Math.max(0, activeIncomePeriod))
    : goalTimeHorizon;

  // Growth Gap Period: Time between investment stop and goal maturity
  const growthGapYears = goalTimeHorizon - investmentHorizon;

  // Calculate what-if future value
  const whatIfFutureValue = futureValue * (1 + whatIfPercentage / 100);

  // Calculate future income at goal time for affordability
  const incomeGrowthRate = userProfile.incomeGrowthRate / 100;
  const expenseGrowthRate = userProfile.expenseGrowthRate / 100;
  const futureIncome = userProfile.monthlyIncome * Math.pow(1 + incomeGrowthRate, timeHorizon);
  const futureExpenses = userProfile.monthlyExpenses * Math.pow(1 + expenseGrowthRate, timeHorizon);
  const futureSurplus = futureIncome - futureExpenses;

  // Calculate required SIP for net amount
  const monthlyReturn = expectedReturn / 100 / 12;
  const totalInvestmentMonths = investmentHorizon * 12;

  // Calculate existing tagged assets future value with their specific returns
  let existingFutureValue = 0;
  if (taggedAssets && taggedAssets.length > 0) {
    taggedAssets.forEach(taggedAsset => {
      const assetAmount = taggedAsset.amount || 0;
      const assetReturn = taggedAsset.expectedReturn || expectedReturn;
      if (assetAmount > 0) {
        existingFutureValue += assetAmount * Math.pow(1 + assetReturn / 100, timeHorizon);
      }
    });
  }

  // Calculate additional SIP future value (TWO-PHASE)
  let sipFutureValue = 0;
  if (sipContribution > 0) {
    if (monthlyReturn > 0) {
      const accumulatedAtInvestEnd = sipContribution * (Math.pow(1 + monthlyReturn, totalInvestmentMonths) - 1) / monthlyReturn;
      sipFutureValue = accumulatedAtInvestEnd * Math.pow(1 + expectedReturn / 100, growthGapYears);
    } else {
      sipFutureValue = sipContribution * totalInvestmentMonths;
    }
  }

  // Calculate lumpsum future value
  let lumpSumFutureValue = 0;
  if (lumpSumContribution > 0) {
    lumpSumFutureValue = lumpSumContribution * Math.pow(1 + expectedReturn / 100, timeHorizon);
  }

  const totalProjectedValue = existingFutureValue + sipFutureValue + lumpSumFutureValue;

  // Calculate the gap
  const remainingGap = Math.max(0, futureValue - totalProjectedValue);
  let requiredSIP = 0;
  let requiredLumpSum = 0;

  if (remainingGap > 0) {
    // TWO-PHASE SIP CALCULATION
    const corpusNeededAtInvestmentEnd = remainingGap / Math.pow(1 + expectedReturn / 100, growthGapYears);
    
    if (corpusNeededAtInvestmentEnd > 0 && totalInvestmentMonths > 0) {
      if (monthlyReturn > 0) {
        requiredSIP = corpusNeededAtInvestmentEnd / ((Math.pow(1 + monthlyReturn, totalInvestmentMonths) - 1) / monthlyReturn);
      } else {
        requiredSIP = corpusNeededAtInvestmentEnd / totalInvestmentMonths;
      }
    }

    requiredLumpSum = remainingGap / Math.pow(1 + expectedReturn / 100, timeHorizon);
  }

  // WHAT-IF SCENARIO (TWO-PHASE)
  const whatIfGap = Math.max(0, whatIfFutureValue - totalProjectedValue);
  let whatIfSIP = 0;
  let whatIfLumpSum = 0;

  if (whatIfGap > 0) {
    const whatIfCorpusNeededAtInvestmentEnd = whatIfGap / Math.pow(1 + expectedReturn / 100, growthGapYears);
    
    if (whatIfCorpusNeededAtInvestmentEnd > 0 && totalInvestmentMonths > 0) {
      if (monthlyReturn > 0) {
        whatIfSIP = whatIfCorpusNeededAtInvestmentEnd / ((Math.pow(1 + monthlyReturn, totalInvestmentMonths) - 1) / monthlyReturn);
      } else {
        whatIfSIP = whatIfCorpusNeededAtInvestmentEnd / totalInvestmentMonths;
      }
    }

    whatIfLumpSum = whatIfGap / Math.pow(1 + expectedReturn / 100, timeHorizon);
  }

  // Calculate achievement probability
  const totalMonthlyCommitment = requiredSIP;
  let achievementProbability = 100;

  if (totalMonthlyCommitment > 0) {
    const affordabilityRatio = totalMonthlyCommitment / futureSurplus;
    
    if (affordabilityRatio <= 0.1) achievementProbability = 100;
    else if (affordabilityRatio <= 0.3) achievementProbability = 95;
    else if (affordabilityRatio <= 0.5) achievementProbability = 85;
    else if (affordabilityRatio <= 0.7) achievementProbability = 75;
    else if (affordabilityRatio <= 1.0) achievementProbability = 60;
    else achievementProbability = 30;

    if (timeHorizon >= 10) achievementProbability = Math.min(100, achievementProbability + 10);
    else if (timeHorizon >= 7) achievementProbability = Math.min(100, achievementProbability + 5);
    else if (timeHorizon <= 3) achievementProbability = Math.max(30, achievementProbability - 10);
  }

  // ACCUMULATION PATH (TWO-PHASE VISUALIZATION)
  // Uses the same monthly-compounding SIP math as the requiredSIP calculation above,
  // so the chart always reconciles with the headline numbers shown to the user.
  const accumulationPath = [];
  for (let year = 1; year <= timeHorizon; year++) {
    const yearlyExistingValue = existingFutureValue > 0 ? existingFutureValue * (year / timeHorizon) : 0;

    const investMonths = Math.min(year, investmentHorizon) * 12;
    const postInvestYears = year - investmentHorizon;

    const monthlySipCorpus = (monthlySip) => {
      if (monthlySip <= 0 || investMonths <= 0) return 0;
      const accumulated = monthlyReturn > 0
        ? monthlySip * ((Math.pow(1 + monthlyReturn, investMonths) - 1) / monthlyReturn)
        : monthlySip * investMonths;
      return postInvestYears > 0
        ? accumulated * Math.pow(1 + expectedReturn / 100, postInvestYears)
        : accumulated;
    };

    const yearlySipValue = monthlySipCorpus(sipContribution);
    const yearlyRequiredSipValue = monthlySipCorpus(requiredSIP);

    const yearlyLumpSumValue = lumpSumContribution > 0
      ? lumpSumContribution * Math.pow(1 + expectedReturn / 100, year)
      : 0;

    accumulationPath.push(yearlyExistingValue + yearlySipValue + yearlyLumpSumValue + yearlyRequiredSipValue);
  }

  return {
    futureValue: Math.round(futureValue),
    whatIfFutureValue: Math.round(whatIfFutureValue),
    whatIfSIP: Math.round(whatIfSIP),
    whatIfLumpSum: Math.round(whatIfLumpSum),
    whatIfPercentage: whatIfPercentage,
    requiredSIP: Math.round(requiredSIP),
    requiredLumpSum: Math.round(requiredLumpSum),
    achievementProbability: Math.round(achievementProbability),
    accumulationPath,
    totalLumpsumNeeded: Math.round(requiredLumpSum),
    affordabilityRatio: totalMonthlyCommitment / futureSurplus,
    futureIncome: Math.round(futureIncome),
    futureExpenses: Math.round(futureExpenses),
    futureSurplus: Math.round(futureSurplus),
    totalMonthlyCommitment: Math.round(totalMonthlyCommitment),
    existingAssetsFutureValue: Math.round(existingFutureValue),
    remainingGap: Math.round(remainingGap),
    totalProjectedValue: Math.round(totalProjectedValue),
    investmentHorizon: investmentHorizon,
    growthGapYears: growthGapYears
  };
}

export function calculateRetirementMetrics(retirementPlan, userProfile) {
  const currentAge = userProfile.currentAge || 30;
  const retirementAge = userProfile.retirementAge || 60;
  
  // Use custom life expectancy from retirement plan if available, otherwise use profile
  const lifeExpectancy = retirementPlan?.lifeExpectancy || userProfile.lifeExpectancy || 80;
  
  const yearsToRetirement = Math.max(0, retirementAge - currentAge);
  const retirementDuration = Math.max(0, lifeExpectancy - retirementAge);
  // Defaults to the "Expected Monthly Expense (Post-Retirement)" set on the
  // profile until the user explicitly customizes it on the retirement plan.
  const monthlyIncomeRequired = retirementPlan?.monthlyIncomeRequired || userProfile.postRetirementMonthlyExpense || 0;
  const inflationRate = userProfile.inflationRates?.general || 6;
  
  // Use custom return expectations from retirement plan if available
  const preRetirementReturn = retirementPlan?.preRetirementReturn || userProfile.returnExpectations?.longTerm || 12;
  const postRetirementReturn = retirementPlan?.postRetirementReturn || 7;

  // Future value of required monthly income at retirement
  const futureMonthlyIncome = monthlyIncomeRequired * Math.pow(1 + inflationRate / 100, yearsToRetirement);
  const annualIncomeRequired = futureMonthlyIncome * 12;

  // Corpus required at retirement (using inflation-adjusted return)
  const realReturn = ((1 + postRetirementReturn / 100) / (1 + inflationRate / 100)) - 1;
  let corpusRequired = 0;

  if (realReturn > 0 && retirementDuration > 0) {
    corpusRequired = annualIncomeRequired * (1 - Math.pow(1 + realReturn, -retirementDuration)) / realReturn;
  } else {
    corpusRequired = annualIncomeRequired * retirementDuration;
  }

  // Future value of existing investments using custom pre-retirement return
  let existingCorpus = 0;
  if (retirementPlan?.existingRetirementInvestments) {
    existingCorpus += retirementPlan.existingRetirementInvestments * Math.pow(1 + preRetirementReturn / 100, yearsToRetirement);
  }

  // Tagged assets with their specific returns
  if (retirementPlan?.taggedAssets && Array.isArray(retirementPlan.taggedAssets)) {
    retirementPlan.taggedAssets.forEach(asset => {
      if (asset.amount > 0 && asset.expectedReturn > 0) {
        existingCorpus += asset.amount * Math.pow(1 + asset.expectedReturn / 100, yearsToRetirement);
      }
    });
  }

  const shortfall = Math.max(0, corpusRequired - existingCorpus);

  // Required SIP using custom pre-retirement return
  const monthlyRate = preRetirementReturn / 100 / 12;
  const totalMonths = yearsToRetirement * 12;
  let requiredSIP = 0;

  if (shortfall > 0 && totalMonths > 0) {
    if (monthlyRate > 0) {
      requiredSIP = shortfall / ((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate);
    } else {
      requiredSIP = shortfall / totalMonths;
    }
  }

  // Required Lumpsum using custom pre-retirement return
  const requiredLumpsum = shortfall > 0 
    ? shortfall / Math.pow(1 + preRetirementReturn / 100, yearsToRetirement)
    : 0;

  // What-if
  const whatIfPercentage = retirementPlan?.whatIfPercentage || 40;
  const whatIfCorpusRequired = corpusRequired * (1 + whatIfPercentage / 100);
  const whatIfShortfall = Math.max(0, whatIfCorpusRequired - existingCorpus);
  
  let whatIfRequiredSIP = 0;
  if (whatIfShortfall > 0 && totalMonths > 0) {
    if (monthlyRate > 0) {
      whatIfRequiredSIP = whatIfShortfall / ((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate);
    } else {
      whatIfRequiredSIP = whatIfShortfall / totalMonths;
    }
  }

  // Growth path using custom pre-retirement return
  const corpusGrowth = [];
  let currentCorpusEst = existingCorpus / Math.pow(1 + preRetirementReturn / 100, yearsToRetirement);
  
  for (let i = 1; i <= yearsToRetirement; i++) {
    const existingGrowth = currentCorpusEst * Math.pow(1 + preRetirementReturn / 100, i);
    const sipGrowth = requiredSIP > 0 
      ? requiredSIP * 12 * ((Math.pow(1 + preRetirementReturn / 100, i) - 1) / (preRetirementReturn / 100))
      : 0;
    corpusGrowth.push(Math.round(existingGrowth + sipGrowth));
  }

  // Cashflow using custom post-retirement return
  const cashflowYears = [];
  const requiredCashflow = [];
  const availableCashflow = [];
  let retirementCorpus = corpusRequired;

  for (let i = 1; i <= retirementDuration; i++) {
    cashflowYears.push(i);
    const req = annualIncomeRequired * Math.pow(1 + inflationRate / 100, i - 1);
    requiredCashflow.push(Math.round(req));
    availableCashflow.push(Math.round(retirementCorpus));
    retirementCorpus = (retirementCorpus * (1 + postRetirementReturn / 100)) - req;
    if (retirementCorpus < 0) retirementCorpus = 0;
  }

  return {
    corpusRequired: Math.round(corpusRequired),
    requiredSIP: Math.round(requiredSIP),
    requiredLumpsum: Math.round(requiredLumpsum),
    shortfall: Math.round(shortfall),
    existingCorpus: Math.round(existingCorpus),
    corpusGrowth,
    whatIfCorpusRequired: Math.round(whatIfCorpusRequired),
    whatIfRequiredSIP: Math.round(whatIfRequiredSIP),
    cashflowYears,
    requiredCashflow,
    availableCashflow,
    finalCorpus: Math.round(retirementCorpus),
    // Include the custom parameters in the return for reference
    lifeExpectancy,
    retirementDuration,
    preRetirementReturn,
    postRetirementReturn
  };
}

export function generateSIPRecommendations(goals, userProfile, retirementMetrics) {
  const recommendations = [];

  if (goals.length === 0) {
    recommendations.push({
      type: 'info',
      title: 'Start Planning',
      text: 'Add your first financial goal to get personalized recommendations.'
    });
  }

  if (retirementMetrics && retirementMetrics.shortfall > 0) {
    recommendations.push({
      type: 'warning',
      title: 'Retirement Gap',
      text: `Prioritize your retirement. Required SIP: ₹${Math.round(retirementMetrics.requiredSIP).toLocaleString()}/month.`
    });
  }

  return recommendations;
}

export function calculatePerformanceScenarios(goal, userProfile) {
  const expectedReturn = goal.expectedReturn || 10;
  const timeHorizon = goal.timeHorizon || 10;
  const requiredSIP = goal.requiredSIP || 0;

  const directReturn = expectedReturn + 1;
  const regularReturn = expectedReturn - 1;

  let directFutureValue = 0;
  let regularFutureValue = 0;
  let standardFutureValue = 0;

  if (requiredSIP > 0) {
    directFutureValue = requiredSIP * 12 * ((Math.pow(1 + directReturn / 100, timeHorizon) - 1) / (directReturn / 100));
    regularFutureValue = requiredSIP * 12 * ((Math.pow(1 + regularReturn / 100, timeHorizon) - 1) / (regularReturn / 100));
    standardFutureValue = requiredSIP * 12 * ((Math.pow(1 + expectedReturn / 100, timeHorizon) - 1) / (expectedReturn / 100));
  }

  return {
    directFundsExtraReturns: Math.round(Math.max(0, directFutureValue - standardFutureValue)),
    regularFundsLoss: Math.round(Math.max(0, standardFutureValue - regularFutureValue)),
    currentPlanReturns: Math.round(standardFutureValue - (requiredSIP * 12 * timeHorizon)),
    outperformanceEarlierBy: 1.5,
    underperformanceDelayBy: 2.0
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Affordability Score (formerly mislabeled "Achievement Probability")
// ═══════════════════════════════════════════════════════════════════════════
// This is NOT a statistical probability of success — no Monte Carlo or market
// simulation runs behind it. It is an honest, deterministic score of how
// comfortably the required SIP fits within your projected income surplus,
// with a small boost/penalty for how much time you have to course-correct.
// Kept as a 0-100 "score" so existing UI (progress bars, badges) still works,
// but labelled and described accurately wherever it is shown.
export function getAffordabilityStatus(score) {
  const value = Math.round(score || 0);
  if (value >= 90) return { label: 'On Track', color: 'green', value };
  if (value >= 70) return { label: 'Needs Attention', color: 'orange', value };
  return { label: 'At Risk', color: 'red', value };
}

export const AFFORDABILITY_SCORE_EXPLANATION =
  'A deterministic score of how comfortably the required monthly SIP fits your projected income surplus, adjusted for how much time you have to course-correct. It is not a statistical probability of achieving the goal.';

export function calculateTotalWhatIfRequirements(goals, retirementMetrics, retirementPlan) {
  const totalWhatIfSIP = goals.reduce((sum, goal) => sum + (goal.whatIfSIP || 0), 0) + (retirementMetrics?.whatIfRequiredSIP || 0);
  const standardSIP = goals.reduce((sum, goal) => sum + (goal.requiredSIP || 0), 0) + (retirementMetrics?.requiredSIP || 0);

  return {
    totalWhatIfSIP: Math.round(totalWhatIfSIP),
    difference: Math.round(totalWhatIfSIP - standardSIP)
  };
}